import React from "react";
import { Trophy, Crown, Medal, Calendar, MapPin } from "lucide-react";

const PILLARS = [
  { key: "strength",  label: "STR" },
  { key: "speed",     label: "SPD" },
  { key: "explosive", label: "EXP" },
  { key: "stamina",   label: "STA" },
  { key: "control",   label: "CTL" },
];

const STATUS_LABEL = { live: "● LIVE", past: "● PAST", upcoming: "UPCOMING" };

const PODIUM = {
  1: { bg: "rgba(250,204,21,0.10)", accent: "#facc15", icon: <Crown size={14} color="#facc15" /> },
  2: { bg: "rgba(203,213,225,0.08)", accent: "#e2e8f0", icon: <Medal size={14} color="#e2e8f0" /> },
  3: { bg: "rgba(217,119,6,0.10)",  accent: "#f59e0b", icon: <Medal size={14} color="#f59e0b" /> },
};

/**
 * Self-contained shareable card for an event leaderboard.
 * Fixed width 1080px so `html-to-image` produces a crisp 1080×N social-friendly PNG.
 * Renders the top 10 entries with the same top-3 highlight vocabulary the on-page table uses.
 */
const EventLeaderboardShareCard = React.forwardRef(function EventLeaderboardShareCard(
  { event, entries, gender, tag, meta, rankedBy = "GPS" },
  ref,
) {
  const top = (entries || []).slice(0, 10);
  const status = event?.status || "upcoming";
  const genderLabel = { M: "MALE", F: "FEMALE" }[gender];
  return (
    <div
      ref={ref}
      style={{
        width: 1280,
        background: "#05070c",
        color: "#fff",
        fontFamily: "'AERO', 'Inter', sans-serif",
        padding: 48,
        boxSizing: "border-box",
        border: "1px solid rgba(0,240,255,0.20)",
      }}
      data-testid="event-leaderboard-share-card"
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
        <div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            letterSpacing: "0.3em",
            color: status === "live" ? "#22d3ee" : "#94a3b8",
            marginBottom: 8,
          }}>
            {tag || `${STATUS_LABEL[status] || "EVENT"} · LEADERBOARD`}{genderLabel ? ` · ${genderLabel}` : ""}
          </div>
          <div style={{
            fontSize: 44,
            fontFamily: "Arial, 'Helvetica Neue', sans-serif",
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
            maxWidth: 780,
            textTransform: "uppercase",
          }}>
            {event?.name || "EVENT"}
          </div>
          <div style={{
            marginTop: 14, display: "flex", gap: 24, alignItems: "center",
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
            color: "#94a3b8", letterSpacing: "0.15em",
          }}>
            {meta ? meta.map((m) => <span key={m}>{m}</span>) : (
              <>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Calendar size={12} /> {event?.date || "—"}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <MapPin size={12} /> {event?.location || "—"}
                </span>
              </>
            )}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{
            fontFamily: "'AERO', sans-serif",
            fontSize: 22, letterSpacing: "0.35em", color: "#fff",
          }}>
            GPS
          </div>
          <div style={{
            marginTop: 4, fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10, letterSpacing: "0.25em", color: "#22d3ee",
          }}>
            GENERAL · PHYSICALITY · SCORE
          </div>
        </div>
      </div>

      {/* Table */}
      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 14,
      }}>
        <thead>
          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)", textAlign: "left" }}>
            <th style={{ padding: 12, letterSpacing: "0.25em", color: "#94a3b8", fontSize: 10 }}>RANK</th>
            <th style={{ padding: 12, letterSpacing: "0.25em", color: "#94a3b8", fontSize: 10 }}>ID</th>
            <th style={{ padding: 12, letterSpacing: "0.25em", color: "#94a3b8", fontSize: 10 }}>NAME</th>
            <th style={{ padding: 12, letterSpacing: "0.25em", color: "#94a3b8", fontSize: 10 }}>INSTITUTION</th>
            <th style={{ padding: 12, letterSpacing: "0.25em", color: "#94a3b8", fontSize: 10 }}>GENDER</th>
            <th style={{ padding: 12, letterSpacing: "0.25em", color: "#94a3b8", fontSize: 10 }}>ATHLETE CLASS</th>
            {PILLARS.map((p) => (
              <th key={p.key} style={{
                padding: 12, letterSpacing: "0.25em", color: "#94a3b8",
                fontSize: 10, textAlign: "right",
              }}>
                {p.label}
              </th>
            ))}
            <th style={{
              padding: 12, letterSpacing: "0.25em", color: "#94a3b8",
              fontSize: 10, textAlign: "right",
            }}>
              GPS
            </th>
          </tr>
        </thead>
        <tbody>
          {top.map((e) => {
            const podium = PODIUM[e.rank];
            return (
              <tr
                key={e.user_id}
                style={{
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                  background: podium?.bg || "transparent",
                  boxShadow: podium ? `inset 3px 0 0 0 ${podium.accent}` : "none",
                }}
              >
                <td style={{ padding: 12, fontWeight: 700, fontSize: 18, color: podium?.accent || "#fff", width: 70 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
                    {e.rank}
                    {podium && podium.icon}
                  </span>
                </td>
                <td style={{ padding: 12, color: "#22d3ee", letterSpacing: "0.15em", fontSize: 11 }}>
                  {e.public_id || "—"}
                </td>
                <td style={{ padding: 12, color: "#fff", fontFamily: "'AERO', sans-serif", fontSize: 16, textTransform: "uppercase" }}>
                  {e.name || "—"}
                </td>
                <td style={{ padding: 12, color: "#cbd5e1", fontSize: 11, maxWidth: 220 }}>
                  {e.gym || "—"}
                </td>
                <td style={{ padding: 12, color: "#94a3b8", fontSize: 11 }}>
                  {e.gender || "—"}
                </td>
                <td style={{ padding: 12, fontSize: 11, maxWidth: 200 }}>
                  {e.archetype?.name ? (
                    <>
                      <div style={{ color: "#fff", letterSpacing: "0.1em" }}>{e.archetype.name.toUpperCase()}</div>
                      {(e.archetype.tag || e.archetype.code) && (
                        <div style={{ color: "#94a3b8", fontSize: 9, letterSpacing: "0.2em", marginTop: 2 }}>
                          {(e.archetype.tag || e.archetype.code).toUpperCase()}
                        </div>
                      )}
                    </>
                  ) : <span style={{ color: "#94a3b8" }}>—</span>}
                </td>
                {PILLARS.map((p) => (
                  <td key={p.key} style={{ padding: 12, color: "#fff", fontSize: 12, textAlign: "right" }}>
                    {e.pillars?.[p.key] != null ? Number(e.pillars[p.key]).toFixed(1) : "—"}
                  </td>
                ))}
                <td style={{
                  padding: 12, fontWeight: 700, fontSize: 18,
                  color: podium?.accent || "#fff", textAlign: "right",
                }}>
                  {e.gps != null ? Number(e.gps).toFixed(1) : "—"}
                </td>
              </tr>
            );
          })}
          {top.length === 0 && (
            <tr>
              <td colSpan={7 + PILLARS.length} style={{
                padding: 48, textAlign: "center",
                color: "#94a3b8", letterSpacing: "0.25em", fontSize: 12,
              }}>
                NO SCORES SUBMITTED YET
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Footer */}
      <div style={{
        marginTop: 28,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
        letterSpacing: "0.25em",
        color: "#64748b",
      }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Trophy size={12} color="#22d3ee" /> TOP {top.length}{genderLabel ? ` · ${genderLabel}` : ""} · RANKED BY {rankedBy}
        </span>
        <span>GENERALPHYSICALITYSCORE · SNAPSHOT</span>
      </div>
    </div>
  );
});

export default EventLeaderboardShareCard;
